/*
 * MultiSelectDropdown — type-to-filter checkbox dropdown for selecting one or
 * more values (e.g. ops agents). Empty selection means "all". Modeled on the
 * twilio_agent RecentCallsFilters multiselect, themed to CSS tokens.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaSearch, FaTimes } from 'react-icons/fa';

function MultiSelectDropdown({ label, options, selected, onChange, placeholder, width }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  let summary = placeholder;
  if (selected.length === 1) {
    summary = options.find((o) => o.value === selected[0])?.label || '1 selected';
  } else if (selected.length > 1) {
    summary = `${selected.length} selected`;
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          minWidth: width,
          padding: '6px 10px',
          borderRadius: 8,
          fontSize: 13,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: selected.length ? 'var(--brand-primary)' : 'var(--input-border)',
          background: 'var(--input-bg)',
          color: selected.length ? 'var(--text-primary)' : 'var(--input-placeholder)'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{label}: </span>
          {summary}
        </span>
        <FaChevronDown size={11} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 30,
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: Math.max(width, 220),
            maxWidth: 320,
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-modal)',
            padding: 8
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: 6,
              border: '1px solid var(--input-border)',
              background: 'var(--input-bg)',
              marginBottom: 6
            }}
          >
            <FaSearch size={11} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13,
                color: 'var(--input-text)'
              }}
            />
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                title="Clear selection"
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: 0,
                  display: 'inline-flex'
                }}
              >
                <FaTimes size={12} />
              </button>
            )}
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-muted)' }}>
                No matches
              </div>
            ) : (
              filtered.map((opt) => (
                <label
                  key={opt.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '2px 8px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    lineHeight: '20px',
                    color: 'var(--text-primary)'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {opt.label}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

MultiSelectDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  width: PropTypes.number
};

MultiSelectDropdown.defaultProps = {
  placeholder: 'All',
  width: 180
};

export default MultiSelectDropdown;
